import classNames from 'classnames';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import UDText from 'ud-ui/components/text';

export interface UDDisclosureProps extends Omit<React.DetailsHTMLAttributes<HTMLDetailsElement>, 'open' | 'children'> {
  /** Заголовок-переключатель: строка или узел (`<Trans>`). */
  summary: React.ReactNode;
  /** Скрытое содержимое; раскладку внутри задаёт вызывающая сторона. */
  children: React.ReactNode;
  /** Раскрыт при первом показе. Дальше состоянием управляет сам `<details>`. */
  defaultOpen?: boolean;
}

/**
 * Раскрывающийся блок: заголовок с шевроном, по клику показывает подробности
 * (progressive disclosure — основная инструкция короткая, детали свёрнуты).
 *
 * Нативный `<details>` / `<summary>`: клавиатура (Enter / Пробел), роль и состояние
 * для скринридера — из коробки, своего `aria-expanded` нет. Браузерный маркер
 * спрятан, вместо него шеврон; кольцо фокуса — только с клавиатуры.
 *
 * Состояние неконтролируемое: `defaultOpen` задаёт только начальное значение.
 */
/**
 * Анимация раскрытия — прогрессивное улучшение на чистом CSS: `::details-content` плавно
 * меняет высоту от 0 до `auto` (`interpolate-size: allow-keywords`) и прозрачность.
 * `content-visibility` с `allow-discrete` держит тело видимым, пока идёт сворачивание,
 * иначе браузер прятал бы его в первый же кадр. Браузер без `::details-content` эти
 * классы игнорирует и раскрывает блок мгновенно — состояние и клавиатура не страдают.
 */
const ANIMATION_CLASSES = [
  '[interpolate-size:allow-keywords]',
  'details-content:h-0 details-content:overflow-hidden details-content:opacity-0',
  'details-content:[transition:height_200ms_ease-out,opacity_200ms_ease-out,content-visibility_200ms_allow-discrete]',
  'open:details-content:h-auto open:details-content:opacity-100',
  'motion-reduce:details-content:transition-none',
];

const UDDisclosure = ({ summary, children, defaultOpen = false, className, ...rest }: UDDisclosureProps) => {
  return (
    <details {...rest} open={defaultOpen} className={classNames('group', ANIMATION_CLASSES, className)}>
      <summary className="text-inkPrimary rounded-control focus-visible:outline-accentNaplesYellow inline-flex cursor-pointer list-none items-center gap-1 focus-visible:outline-2 focus-visible:outline-offset-2 [&::-webkit-details-marker]:hidden">
        <ChevronRight
          size={16}
          aria-hidden
          className="shrink-0 transition-transform duration-200 ease-out group-open:rotate-90 motion-reduce:transition-none"
        />
        {/* Типографику заголовка и тела задаёт кит, а не экран: иначе тот же блок в другом месте
            получит другой кегль. `relaxed` у тела — межстрочный для многострочной подсказки. */}
        <UDText as="span" variant="text-2-bold" color="inherit">
          {summary}
        </UDText>
      </summary>
      {/* Отступ слева = шеврон 16 px + зазор 4 px: тело выровнено по тексту заголовка.
          Сверху — padding, а не margin: высота анимируется от нуля без скачка в конце. */}
      <UDText as="div" variant="text-2" relaxed className="text-inkSecondary pt-3 pl-5">
        {children}
      </UDText>
    </details>
  );
};

export default UDDisclosure;
